import { Children, cloneElement, isValidElement } from 'react'

import { ABO_LABEL, PAGE_TYPE_SEARCH_RESULTS, TENANT_IT } from 'shared/consts'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import {
  CAT_REMOVE_CTA_AND_VARIANTS,
  EXPERIMENT_VARIATION,
  SERP_MOBILE_LAYOUT_IT,
  SERP_REMOVE_CTA_AND_VARIANTS,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import { getProductTemplate } from 'shared/utils/productTemplateUtils'
import { promotionTypeMapping } from 'views/consts/promotions'
import {
  isHideAboSubscription,
  isRedPointsActive,
} from 'views/utils/featureToggleUtils'
import { shouldRenderLabel } from 'views/utils/redPointsUtils'

export const shouldRenderProductLabels = ({ redPointsData, ...rest }) => {
  return (
    Object.values(rest)
      .concat(redPointsData && shouldRenderLabel(redPointsData))
      .filter(value => value === true || value?.length).length > 0
  )
}

/**
 * Helper adds a special style class for first <mobileLabelsInitialNumber> labels
 * that are not hidden with 'aria-hidden'. It has CSS rules that hide other labels
 * until the client script is loaded.
 * Client script removes the class and applies viewport size calculations,
 * so labels row is never overflowing the product card.
 *
 * @see app/views/assets/scripts/searchResultsHandlers.js#bindMobileLabels
 * @see app/views/components/organisms/FilteredProductList/SearchProductListItem/SearchProductListItem.scss
 */
export const decorateLabelsWithExtraMobileClasses = (
  labels,
  mobileLabelsInitialNumber
) => {
  let counter = mobileLabelsInitialNumber

  return Children.map(labels, label => {
    if (
      counter &&
      isValidElement(label) &&
      label.props['aria-hidden'] !== true &&
      label.props['aria-hidden'] !== 'true'
    ) {
      counter -= 1

      return cloneElement(label, {
        className: `${label.props.className} no-hide`,
      })
    }

    return label
  })
}

export const isSaeOffer = ({ experiments, bestOffer, tenant }) => {
  return (
    !isMarketplace(experiments, tenant) ||
    bestOffer?.type === OFFER_TYPE_SAE ||
    bestOffer?.offerType === OFFER_TYPE_SAE
  )
}

export const shouldRenderAboLabel = ({ labels, offerTypeSae }) => {
  if (!labels.includes(ABO_LABEL)) {
    return false
  }

  return offerTypeSae
}

export const hasPersonalVariantAttributes = variantAttributes => {
  return (
    Array.isArray(variantAttributes) &&
    variantAttributes.some(variantAttribute => variantAttribute?.personal)
  )
}

/*
 * props redPoints, redPointsMultiplierIsValid, redPointsIsActive aren't provided from algolia
 * if the multiplier is provided and is greater than 1 => always show the label
 * we need to set the rest of the props manually to pass the shouldRenderLabel check
 */
export const getDummyRedpointsData = (multiplierRP, product, tenant) => {
  const productTemplate = getProductTemplate({ ...product, tenant })

  return {
    multiplierRP,
    redPoints: 1,
    redPointsMultiplierIsValid: true,
    redPointsIsActive: true,
    productTemplate,
  }
}

export const serpMobileExperimentEnabledAndV3 = (
  experiments,
  isSearchPage,
  isMobile
) => {
  const serpMobileLayoutITExperiment = getExperiment(
    SERP_MOBILE_LAYOUT_IT,
    experiments
  )

  return (
    serpMobileLayoutITExperiment.isEnabled &&
    isMobile &&
    isSearchPage &&
    serpMobileLayoutITExperiment?.variant === EXPERIMENT_VARIATION.V3
  )
}

export const collectLabelsProps = (
  product,
  enableSponsoredLabel,
  publicRuntimeConfig,
  adServerMetadata,
  showDiscountLabel = false
) => {
  const {
    pageProperties: { experiments = [], featureToggles },
    publicConfig: { tenant },
  } = publicRuntimeConfig

  const {
    best_offer: bestOffer,
    best_offer: {
      promotion: bestOfferPromotion,
      redpoints: { multiplier } = {},
      type,
    } = {},
    id,
    labels,
    nowProductGroup,
    flags: { prescriptionMedicine },
    multiplierRP,
    objectID,
    promotion,
    promotionProduct,
    sponsored,
    upid,
  } = product

  const offerTypeSae = isSaeOffer({
    bestOffer,
    experiments,
    tenant,
  })

  const hideAboSubscription = isHideAboSubscription(featureToggles)
  const displayAboLabel =
    Boolean(labels?.includes(ABO_LABEL)) && !hideAboSubscription

  let shouldDisplayNowWidget = nowProductGroup === 'sameday_nextday'

  if (shouldDisplayNowWidget && isMarketplace(experiments, tenant)) {
    shouldDisplayNowWidget = type === OFFER_TYPE_SAE
  }
  const rpMultiplier = multiplierRP || multiplier
  const isRedPointsFeatureToggleActive = isRedPointsActive(featureToggles)
  const { trackingUrls = {} } = adServerMetadata[objectID] || {}

  return {
    displayAboLabel,
    displayNowWidget: shouldDisplayNowWidget,
    offerTypeSae,
    otherLabels: labels?.filter(labelName => labelName !== ABO_LABEL) || [],
    prescription: prescriptionMedicine,
    promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
    promotionProduct,
    redPointsData:
      isRedPointsFeatureToggleActive &&
      multiplierRP &&
      getDummyRedpointsData(rpMultiplier, product, tenant),
    sponsored: enableSponsoredLabel && sponsored,
    showDiscountLabel,
    upid,
    trackingUrls,
    id,
  }
}

export const shouldRenderCta = (experiments, pageType) => {
  const getIsAllowedToRenderCta = experiment => {
    return (
      !experiment?.isEnabled ||
      (experiment?.isEnabled &&
        experiment?.variant === EXPERIMENT_VARIATION.DEFAULT)
    )
  }
  if (pageType === PAGE_TYPE_SEARCH_RESULTS) {
    const removeCtaAndVariantsSerpExperiment = getExperiment(
      SERP_REMOVE_CTA_AND_VARIANTS,
      experiments
    )
    return getIsAllowedToRenderCta(removeCtaAndVariantsSerpExperiment)
  }
  const removeCtaAndVariantsCatExperiment = getExperiment(
    CAT_REMOVE_CTA_AND_VARIANTS,
    experiments
  )

  return getIsAllowedToRenderCta(removeCtaAndVariantsCatExperiment)
}

export const shouldRenderVariants = (experiments, pageType) => {
  const getIsAllowedToRenderVariants = experiment => {
    return (
      !experiment.isEnabled ||
      experiment.variant === EXPERIMENT_VARIATION.DEFAULT ||
      experiment.variant === EXPERIMENT_VARIATION.V3
    )
  }
  if (pageType === PAGE_TYPE_SEARCH_RESULTS) {
    const removeCtaAndVariantsSerpExperiment = getExperiment(
      SERP_REMOVE_CTA_AND_VARIANTS,
      experiments
    )
    return getIsAllowedToRenderVariants(removeCtaAndVariantsSerpExperiment)
  }
  const removeCtaAndVariantsCatExperiment = getExperiment(
    CAT_REMOVE_CTA_AND_VARIANTS,
    experiments
  )

  return getIsAllowedToRenderVariants(removeCtaAndVariantsCatExperiment)
}

export const shouldRenderProductDetailLink = (
  experiments,
  pageType,
  tenant
) => {
  const getIsAllowedToRenderProductDetailLink = experiment => {
    return (
      experiment?.isEnabled && experiment?.variant === EXPERIMENT_VARIATION.V2
    )
  }

  if (pageType === PAGE_TYPE_SEARCH_RESULTS) {
    const removeCtaAndVariantsSerpExperiment = getExperiment(
      SERP_REMOVE_CTA_AND_VARIANTS,
      experiments
    )

    if (!removeCtaAndVariantsSerpExperiment?.isEnabled) {
      return
    }

    return getIsAllowedToRenderProductDetailLink(
      removeCtaAndVariantsSerpExperiment
    )
  }
  const removeCtaAndVariantsCatExperiment = getExperiment(
    CAT_REMOVE_CTA_AND_VARIANTS,
    experiments
  )
  if (!removeCtaAndVariantsCatExperiment?.isEnabled) {
    return
  }

  if (tenant === TENANT_IT) {
    return (
      removeCtaAndVariantsCatExperiment.variant === EXPERIMENT_VARIATION.V2 ||
      removeCtaAndVariantsCatExperiment.variant === EXPERIMENT_VARIATION.DEFAULT
    )
  }
  return getIsAllowedToRenderProductDetailLink(
    removeCtaAndVariantsCatExperiment
  )
}

export const shouldRenderAbPrice = (experiments, pageType) => {
  const getIsAllowedToRenderAbPrice = experiment => {
    return (
      (experiment.isEnabled &&
        experiment.variant === EXPERIMENT_VARIATION.V1) ||
      experiment.variant === EXPERIMENT_VARIATION.V2
    )
  }
  if (pageType === PAGE_TYPE_SEARCH_RESULTS) {
    const removeCtaAndVariantsSerpExperiment = getExperiment(
      SERP_REMOVE_CTA_AND_VARIANTS,
      experiments
    )
    return getIsAllowedToRenderAbPrice(removeCtaAndVariantsSerpExperiment)
  }
  const removeCtaAndVariantsCatExperiment = getExperiment(
    CAT_REMOVE_CTA_AND_VARIANTS,
    experiments
  )

  return getIsAllowedToRenderAbPrice(removeCtaAndVariantsCatExperiment)
}
